import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useLazyQuery, useMutation } from '@apollo/client'
import { t } from 'i18next'
import { GET_BOATS_FOR_CHARTER, GET_CONFIG_STATUS_LOOKUP, GET_CHARTER_BOAT_CONFIG } from 'graphql/charterBoats/queries'
import { CREATE_CHARTER_BOAT_CONFIG, UPDATE_CHARTER_BOAT_CONFIG } from 'graphql/charterBoats/mutations'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { Input } from 'components/input'
import { Divider } from 'components/divider'
import { ErrorDialog, SuccessDialog } from 'components/custom/ResponseDialog'
import { ArrowLeftIcon } from '@heroicons/react/20/solid'
import { BoatIcon } from 'components/icons'
import loadingImage from '../../../assets/images/boat-loading.gif'

export default function ConfigurationForm() {
    const navigate = useNavigate()
    const { id } = useParams()
    const isEditMode = Boolean(id)
    
    // State management
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [successMessage, setSuccessMessage] = useState('')
    const [boats, setBoats] = useState([])
    const [statusOptions, setStatusOptions] = useState([])
    const [loading, setLoading] = useState(false)
    
    // Load boats and status options
    const [getBoats] = useLazyQuery(GET_BOATS_FOR_CHARTER, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data.getBoatsForCharter.success && data.getBoatsForCharter.code === '200') {
                setBoats(data.getBoatsForCharter.boats)
            } else {
                setErrorMessage(data.getBoatsForCharter.message || t('Failed to fetch boats'))
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setErrorMessage(error.message || t('Failed to fetch boats'))
            setShowErrDialog(true)
        }
    })

    const [getStatusLookup] = useLazyQuery(GET_CONFIG_STATUS_LOOKUP, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data.getConfigStatusLookup.success && data.getConfigStatusLookup.code === '200') {
                setStatusOptions(data.getConfigStatusLookup.statuses)
            } else {
                setErrorMessage(data.getConfigStatusLookup.message || t('Failed to fetch status options'))
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setErrorMessage(error.message || t('Failed to fetch status options'))
            setShowErrDialog(true)
        }
    })

    useEffect(() => {
        getBoats()
        getStatusLookup()
    }, [])
    
    // Load existing config in edit mode
    const [getConfig] = useLazyQuery(GET_CHARTER_BOAT_CONFIG, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data.getCharterBoatConfig.success && data.getCharterBoatConfig.code === '200') {
                formik.setValues(data.getCharterBoatConfig.configuration)
            } else {
                setErrorMessage(data.getCharterBoatConfig.message || t('Failed to fetch configuration'))
                setShowErrDialog(true)
            }
            setLoading(false)
        },
        onError: error => {
            setErrorMessage(error.message || t('Failed to fetch configuration'))
            setShowErrDialog(true)
            setLoading(false)
        }
    })

    useEffect(() => {
        if (isEditMode) {
            setLoading(true)
            getConfig({
                variables: { id }
            })
        }
    }, [id, isEditMode])
    
    // Form validation schema
    const validationSchema = Yup.object({
        boat_id: Yup.string().required(t('Boat is required')),
        from_date: Yup.date().required(t('From date is required')),
        to_date: Yup.date()
            .nullable()
            .min(Yup.ref('from_date'), t('To date must be after from date')),
        base_price: Yup.number()
            .required(t('Base price is required'))
            .positive(t('Base price must be positive'))
            .min(0.001, t('Base price must be greater than 0')),
        status_code: Yup.string().required(t('Status is required'))
    })
    
    // Create mutation
    const [createConfig] = useMutation(CREATE_CHARTER_BOAT_CONFIG, {
        onCompleted: data => {
            if (data.createCharterBoatConfig.success && data.createCharterBoatConfig.code === '200') {
                setSuccessMessage(t('Configuration created successfully'))
                setShowSucDialog(true)
                setTimeout(() => navigate('/charter-boats/configuration'), 1500)
            } else {
                setErrorMessage(data.createCharterBoatConfig.message || t('Failed to create configuration'))
                setShowErrDialog(true)
            }
            setLoading(false)
        },
        onError: error => {
            setErrorMessage(error.message || t('Failed to create configuration'))
            setShowErrDialog(true)
            setLoading(false)
        }
    })

    // Update mutation
    const [updateConfig] = useMutation(UPDATE_CHARTER_BOAT_CONFIG, {
        onCompleted: data => {
            if (data.updateCharterBoatConfig.success && data.updateCharterBoatConfig.code === '200') {
                setSuccessMessage(t('Configuration updated successfully'))
                setShowSucDialog(true)
                setTimeout(() => navigate('/charter-boats/configuration'), 1500)
            } else {
                setErrorMessage(data.updateCharterBoatConfig.message || t('Failed to update configuration'))
                setShowErrDialog(true)
            }
            setLoading(false)
        },
        onError: error => {
            setErrorMessage(error.message || t('Failed to update configuration'))
            setShowErrDialog(true)
            setLoading(false)
        }
    })

    // Form handling
    const formik = useFormik({
        initialValues: {
            boat_id: '',
            from_date: '',
            to_date: '',
            base_price: '',
            status_code: 'ACT'
        },
        validationSchema: validationSchema,
        onSubmit: values => {
            setLoading(true)
            const input = {
                boat_id: values.boat_id,
                from_date: values.from_date,
                to_date: values.to_date || null,
                base_price: parseFloat(values.base_price),
                status_code: values.status_code
            }
            
            if (isEditMode) {
                updateConfig({
                    variables: {
                        id,
                        input
                    }
                })
            } else {
                createConfig({
                    variables: {
                        input
                    }
                })
            }
        }
    })
    
    const handleDialogClose = () => {
        setShowSucDialog(false)
        setShowErrDialog(false)
        setSuccessMessage('')
        setErrorMessage('')
    }
    
    const handleCancel = () => {
        navigate('/charter-boats/configuration')
    }
    
    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <button
                    onClick={() => navigate('/charter-boats/configuration')}
                    type="button"
                    className="inline-flex items-center gap-x-1.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Back to Configurations')}
                </button>
            </div>
            
            <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
                <h1 className="text-base font-semibold leading-7 text-gray-900 flex items-center">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 bg-[#e9eff1] w-[40px] h-[40px]">
                        <BoatIcon className="w-[20px] h-[20px] fill-[#285f74]" />
                    </div>
                    <div>
                        <span className="ms-3">
                            {isEditMode ? t('Edit Configuration') : t('Add New Configuration')}
                        </span>
                        <p className="ms-3 text-sm font-normal leading-6 weight-[200] text-gray-500">
                            {isEditMode 
                                ? t('Update charter boat pricing configuration') 
                                : t('Create new charter boat pricing configuration')
                            }
                        </p>
                    </div>
                </h1>
            </div>
            
            <Divider />
            
            {loading && (
                <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                    <img alt="logo" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">Loading, please wait...</p>
                </div>
            )}
            
            {!loading && (
                <div className="bg-white shadow sm:rounded-lg mt-6">
                    <div className="px-4 py-6 sm:p-6">
                        <form onSubmit={formik.handleSubmit}>
                            <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                                
                                {/* Boat Selection */}
                                <div className="sm:col-span-3">
                                    <label htmlFor="boat_id" className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Boat')} <span className="text-red-500">*</span>
                                    </label>
                                    <div className="mt-2">
                                        <select
                                            id="boat_id"
                                            name="boat_id"
                                            value={formik.values.boat_id}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            className={`block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ${
                                                formik.touched.boat_id && formik.errors.boat_id
                                                    ? 'ring-red-500'
                                                    : 'ring-gray-300'
                                            } focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6`}
                                        >
                                            <option value="">{t('Select a boat')}</option>
                                            {boats.map(boat => (
                                                <option key={boat.id} value={boat.id}>
                                                    {boat.name.en}
                                                </option>
                                            ))}
                                        </select>
                                        {formik.touched.boat_id && formik.errors.boat_id && (
                                            <p className="mt-2 text-sm text-red-600">{formik.errors.boat_id}</p>
                                        )}
                                    </div>
                                </div>
                                
                                {/* Status */}
                                <div className="sm:col-span-3">
                                    <label htmlFor="status_code" className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Status')} <span className="text-red-500">*</span>
                                    </label>
                                    <div className="mt-2">
                                        <select
                                            id="status_code"
                                            name="status_code"
                                            value={formik.values.status_code}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            className={`block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ${
                                                formik.touched.status_code && formik.errors.status_code
                                                    ? 'ring-red-500'
                                                    : 'ring-gray-300'
                                            } focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6`}
                                        >
                                            <option value="">{t('Select status')}</option>
                                            {statusOptions.map(status => (
                                                <option key={status.code} value={status.code}>
                                                    {status.description.en}
                                                </option>
                                            ))}
                                        </select>
                                        {formik.touched.status_code && formik.errors.status_code && (
                                            <p className="mt-2 text-sm text-red-600">{formik.errors.status_code}</p>
                                        )}
                                    </div>
                                </div>
                                
                                {/* From Date */}
                                <div className="sm:col-span-3">
                                    <label htmlFor="from_date" className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('From Date')} <span className="text-red-500">*</span>
                                    </label>
                                    <div className="mt-2">
                                        <Input
                                            type="date"
                                            id="from_date"
                                            name="from_date"
                                            value={formik.values.from_date}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            className={
                                                formik.touched.from_date && formik.errors.from_date
                                                    ? 'ring-red-500'
                                                    : ''
                                            }
                                        />
                                        {formik.touched.from_date && formik.errors.from_date && (
                                            <p className="mt-2 text-sm text-red-600">{formik.errors.from_date}</p>
                                        )}
                                    </div>
                                </div>
                                
                                {/* To Date */}
                                <div className="sm:col-span-3">
                                    <label htmlFor="to_date" className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('To Date')} <span className="text-gray-500">(Optional)</span>
                                    </label>
                                    <div className="mt-2">
                                        <Input
                                            type="date"
                                            id="to_date"
                                            name="to_date"
                                            value={formik.values.to_date}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            className={
                                                formik.touched.to_date && formik.errors.to_date
                                                    ? 'ring-red-500'
                                                    : ''
                                            }
                                        />
                                        {formik.touched.to_date && formik.errors.to_date && (
                                            <p className="mt-2 text-sm text-red-600">{formik.errors.to_date}</p>
                                        )}
                                    </div>
                                </div>
                                
                                {/* Base Price */}
                                <div className="sm:col-span-3">
                                    <label htmlFor="base_price" className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Base Price (BHD)')} <span className="text-red-500">*</span>
                                    </label>
                                    <div className="mt-2">
                                        <Input
                                            type="number"
                                            id="base_price"
                                            name="base_price"
                                            step="0.001"
                                            min="0"
                                            value={formik.values.base_price}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            placeholder="0.000"
                                            className={
                                                formik.touched.base_price && formik.errors.base_price
                                                    ? 'ring-red-500'
                                                    : ''
                                            }
                                        />
                                        {formik.touched.base_price && formik.errors.base_price && (
                                            <p className="mt-2 text-sm text-red-600">{formik.errors.base_price}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                            
                            {/* Action Buttons */}
                            <div className="mt-8 flex items-center justify-end gap-x-3">
                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                >
                                    {t('Cancel')}
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading || !formik.isValid}
                                    className="rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {loading ? t('Saving...') : isEditMode ? t('Update') : t('Submit')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            
            {/* Dialogs */}
            {showErrDialog && (
                <ErrorDialog status={true} title="Error" message={errorMessage} onClose={handleDialogClose} />
            )}
            {showSucDialog && (
                <SuccessDialog status={true} title="Success" message={successMessage} onClose={handleDialogClose} />
            )}
        </>
    )
}
